import type React from 'react';
import { CredentialModalView } from './CredentialModalView';
import { useCredentialForm } from './useCredentialForm';

type CredentialModalProps = {
    slot: string;
    title: string;
    description: string;
    label: string;
    placeholder: string;
    submitLabel: string;
    cancelLabel: string;
    inputType?: 'text' | 'password';
    inputMode?: 'numeric' | 'text';
    error: string | null;
    isOpen: boolean;
    isSubmitting: boolean;
    validate: (value: string) => boolean;
    sanitize?: (value: string) => string;
    onClose: () => void;
    onSubmit: (value: string) => void;
};

/**
 * Single-field modal reused for Set-PIN and Reset-Password. Container wires
 * {@link useCredentialForm} (per-use `validate` / `sanitize` + submit) into the
 * render-only `CredentialModalView`.
 */
export const CredentialModal: React.FC<CredentialModalProps> = ({
    slot,
    title,
    description,
    label,
    placeholder,
    submitLabel,
    cancelLabel,
    inputType = 'text',
    inputMode,
    error,
    isOpen,
    isSubmitting,
    validate,
    sanitize,
    onClose,
    onSubmit,
}) => {
    const {
        value,
        isValid,
        onChange,
        onSubmit: onSubmitField,
    } = useCredentialForm({ isOpen, validate, sanitize, onSubmit });

    return (
        <CredentialModalView
            cancelLabel={cancelLabel}
            description={description}
            error={error}
            inputMode={inputMode}
            inputType={inputType}
            isOpen={isOpen}
            isSubmitting={isSubmitting}
            isValid={isValid}
            label={label}
            onChange={onChange}
            onClose={onClose}
            onSubmit={onSubmitField}
            placeholder={placeholder}
            slot={slot}
            submitLabel={submitLabel}
            title={title}
            value={value}
        />
    );
};
